import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../api';
import { formatIST } from '../utils/formatDate';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600',
  go: 'bg-blue-100 text-blue-700',
  running: 'bg-green-100 text-green-700',
  stopped: 'bg-yellow-100 text-yellow-700',
  archived: 'bg-gray-100 text-gray-400',
  failed: 'bg-red-100 text-red-700',
};

export default function AdIdeaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [idea, setIdea] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const data = await api.getAd(id);
      setIdea(data);
      setForm(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const saveEdits = async (extra = {}) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const fd = new FormData();
      const payload = { ...form, ...extra, existing_creative_path: idea.creative_path || '' };
      Object.entries(payload).forEach(([k, v]) => {
        if (v !== null && v !== undefined) fd.append(k, typeof v === 'object' ? JSON.stringify(v) : v);
      });
      const updated = await api.updateAd(id, fd);
      setIdea(updated);
      setForm(updated);
      setNotice('Saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleGoLive = () => {
    if (!window.confirm('Launch this ad? It will go live on the next scheduled run.')) return;
    saveEdits({ status: 'go' });
  };

  const handleToggle = async (action) => {
    let confirmMsg = action === 'pause'
      ? 'Pause this ad on the platform?'
      : 'Enable this ad — it will start serving on the platform?';
    if (idea.source === 'platform') {
      confirmMsg = `⚠️ This campaign was DISCOVERED from the platform, not created here. ${confirmMsg}`;
    }
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.toggleStatus(id, action);
      await load();
      setNotice(action === 'pause' ? 'Paused.' : 'Enabled.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleResubmit = () => {
    if (!window.confirm('Resubmit this idea for launch?')) return;
    saveEdits({ status: 'go', failure_reason: null });
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this idea permanently?')) return;
    await api.deleteAd(id);
    navigate('/ads/status');
  };

  if (error && !idea) {
    return (
      <Layout>
        <div className="bg-red-50 text-red-600 text-sm rounded-lg px-3 py-2">{error}</div>
      </Layout>
    );
  }

  if (!idea || !form) {
    return <Layout><p className="text-gray-500">Loading...</p></Layout>;
  }

  const canGoLive = form.status === 'draft' && idea.creative_status === 'approved';
  const canResubmit = form.status === 'failed';

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/ads/status" className="text-sm text-brand-purple hover:underline">&larr; Back to Ads Status</Link>
          <h1 className="text-2xl font-bold text-gray-800 mt-1">{idea.headline}</h1>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm ${STATUS_COLORS[idea.status] || 'bg-gray-100 text-gray-500'}`}>
          {idea.status}
        </span>
      </div>

      {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg px-3 py-2 mb-4">{error}</div>}
      {notice && <div className="bg-green-50 text-green-700 text-sm rounded-lg px-3 py-2 mb-4">{notice}</div>}

      {idea.status === 'failed' && idea.failure_reason && (
        <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">
          <strong>Failure reason:</strong> {idea.failure_reason}
        </div>
      )}

      {idea.status === 'draft' && idea.creative_status !== 'approved' && (
        <div className="bg-yellow-50 text-yellow-700 text-sm rounded-lg px-4 py-3 mb-4">
          Creative status: <strong>{idea.creative_status}</strong> — this idea can't go live until the creative is approved.
        </div>
      )}

      <div className="flex gap-3 mb-6">
        {canGoLive && (
          <button onClick={handleGoLive} disabled={busy}
            className="bg-green-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50">
            🚀 Go Live
          </button>
        )}
        {idea.status === 'running' && (
          <button onClick={() => handleToggle('pause')} disabled={busy}
            className="bg-yellow-500 text-white px-5 py-2 rounded-lg font-medium hover:bg-yellow-600 disabled:opacity-50">
            ⏸ Pause
          </button>
        )}
        {idea.status === 'stopped' && (
          <button onClick={() => handleToggle('enable')} disabled={busy}
            className="bg-green-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50">
            ▶ Enable
          </button>
        )}
        {canResubmit && (
          <button onClick={handleResubmit} disabled={busy}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
            🔁 Resubmit
          </button>
        )}
        <button onClick={handleDelete}
          className="text-red-500 px-5 py-2 rounded-lg font-medium hover:bg-red-50">
          Delete
        </button>
      </div>

      <div className="bg-white rounded-xl shadow p-6 grid grid-cols-2 gap-4 max-w-3xl mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
          <input name="brand" value={form.brand || ''} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
          <input value={form.platform} disabled
            className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-gray-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Daily Budget (₹)</label>
          <input type="number" step="0.01" name="daily_budget" value={form.daily_budget || ''} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Lifetime Cap (₹)</label>
          <input type="number" step="0.01" name="lifetime_cap" value={form.lifetime_cap || ''} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target CPA (₹)</label>
          <input type="number" step="0.01" name="target_cpa" value={form.target_cpa || ''} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target ROAS</label>
          <input type="number" step="0.01" name="target_roas" value={form.target_roas || ''} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input type="date" name="start_date" value={form.start_date ? form.start_date.slice(0,10) : ''} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input type="date" name="end_date" value={form.end_date ? form.end_date.slice(0,10) : ''} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Ad Copy</label>
          <textarea name="creative_copy" value={form.creative_copy || ''} onChange={handleChange} rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div className="col-span-2">
          <button onClick={() => saveEdits()} disabled={busy}
            className="bg-brand-purple text-white px-5 py-2 rounded-lg font-medium hover:bg-brand-purpleDark disabled:opacity-50">
            Save Changes
          </button>
          <span className="text-xs text-gray-400 ml-3">
            Changes to budget/CPA apply on the next scheduled monitoring run, not instantly.
          </span>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6 max-w-3xl text-sm text-gray-600 space-y-1">
        <div>Platform Campaign ID: <span className="font-mono">{idea.platform_campaign_id || '—'}</span></div>
        <div>Platform Ad ID: <span className="font-mono">{idea.platform_ad_id || '—'}</span></div>
        <div>Last Spend Recorded: ₹{idea.last_spend ?? '—'}</div>
        <div>Last Synced: {formatIST(idea.last_synced_at)}</div>
        <div>Created: {formatIST(idea.created_at)}</div>
        <div>Last Updated: {formatIST(idea.updated_at)}</div>
        <div>Creative Status: {idea.creative_status}</div>
      </div>
    </Layout>
  );
}
